import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Avatar,
} from '@mui/material';
import { useAuth } from '../hooks/useAuth';
import { changePassword } from '../services/authService';
import showNotification from '../components/common/Notification';

/** User profile — password change and assigned brands */
const Profile = () => {
  const { user } = useAuth();
  const [passwords, setPasswords] = useState({ current: '', next: '', confirm: '' });
  const [saving, setSaving] = useState(false);

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();

    if (passwords.next.length < 6) {
      showNotification('Yeni şifre en az 6 karakter olmalıdır', 'error');
      return;
    }
    if (passwords.next !== passwords.confirm) {
      showNotification('Yeni şifreler eşleşmiyor', 'error');
      return;
    }

    try {
      setSaving(true);
      await changePassword(passwords.current, passwords.next);
      showNotification('Şifreniz güncellendi', 'success');
      setPasswords({ current: '', next: '', confirm: '' });
    } catch (err) {
      const msg = err.response?.data?.message || err.response?.data?.error || 'Şifre güncellenemedi';
      showNotification(msg, 'error');
    } finally {
      setSaving(false);
    }
  };

  const brands = user?.brands || [];
  const roleLabel =
    user?.role === 'admin'
      ? 'Yönetici'
      : user?.role === 'editor'
        ? 'Editör'
        : user?.role === 'stakeholder'
          ? 'Stakeholder'
          : 'Kullanıcı';

  return (
    <Box sx={{ maxWidth: 640 }}>
      <Typography variant="h5" sx={{ fontWeight: 700, color: '#240B4B', mb: 3 }}>
        Profil
      </Typography>

      <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Avatar sx={{ width: 56, height: 56, bgcolor: '#240B4B', fontSize: 24 }}>
            {user?.username?.charAt(0).toUpperCase()}
          </Avatar>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {user?.username}
            </Typography>
            <Chip label={roleLabel} size="small" sx={{ mt: 0.5 }} />
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, color: '#240B4B' }}>
          Atanmış Markalar
        </Typography>
        {user?.role === 'admin' ? (
          <Typography variant="body2" color="text.secondary">
            Yönetici olarak tüm markalara erişiminiz var.
          </Typography>
        ) : brands.length > 0 ? (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {brands.map((brand) => (
              <Chip key={brand._id || brand.id} label={brand.name} variant="outlined" />
            ))}
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Henüz size atanmış marka bulunmuyor.
          </Typography>
        )}
      </Paper>

      <Paper sx={{ p: 3, borderRadius: 2 }} component="form" onSubmit={handlePasswordSubmit}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#240B4B', mb: 2 }}>
          Şifre Değiştir
        </Typography>

        <Box sx={{ display: 'grid', gap: 2 }}>
          <TextField
            label="Mevcut Şifre"
            type="password"
            value={passwords.current}
            onChange={(e) => setPasswords((p) => ({ ...p, current: e.target.value }))}
            required
            size="small"
            fullWidth
          />
          <TextField
            label="Yeni Şifre"
            type="password"
            value={passwords.next}
            onChange={(e) => setPasswords((p) => ({ ...p, next: e.target.value }))}
            required
            size="small"
            fullWidth
          />
          <TextField
            label="Yeni Şifre (Tekrar)"
            type="password"
            value={passwords.confirm}
            onChange={(e) => setPasswords((p) => ({ ...p, confirm: e.target.value }))}
            required
            size="small"
            fullWidth
          />
          <Button
            type="submit"
            variant="contained"
            disabled={saving}
            sx={{ alignSelf: 'flex-start', bgcolor: '#240B4B', '&:hover': { bgcolor: '#3d1c6b' } }}
          >
            {saving ? <CircularProgress size={20} color="inherit" /> : 'Şifreyi Güncelle'}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default Profile;
